import * as logger from 'loglevel'
import { sanitizeAnearHtml } from './sanitizeAnearHtml'
import { upgradeCustomElements } from '../WebComponents'

const normalizeType = (type) => (type === 'append' || type === 'replace' ? type : 'replace')
const normalizeContentType = (contentType) => (contentType === 'text' ? 'text' : 'html')
const escapeAnchorName = (name) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(name)
  }
  return String(name).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

const parseTargetUpdate = (contentOrObj) => {
  if (typeof contentOrObj === 'string') {
    return { content: contentOrObj, type: 'replace', contentType: 'html' }
  }

  if (contentOrObj && typeof contentOrObj === 'object') {
    const content = contentOrObj.content
    if (content === undefined || content === null) return null
    return {
      content: typeof content === 'string' ? content : String(content),
      type: normalizeType(contentOrObj.type),
      contentType: normalizeContentType(contentOrObj.contentType),
    }
  }

  return null
}

/**
 * Apply anear-anchor target updates to an already-rendered frame root.
 * Returns true only when at least one target was applied.
 */
export const applyAnchorTargets = (root, targets, options = {}) => {
  if (!root || targets == null || typeof targets !== 'object' || Array.isArray(targets)) {
    return false
  }

  const logPrefix = options.logPrefix || '[ABR]'
  let anyApplied = false

  for (const [anchor, contentOrObj] of Object.entries(targets)) {
    const sel = `anear-anchor[name="${escapeAnchorName(anchor)}"]`
    const el = root.querySelector(sel)

    if (!el) {
      logger.debug(`${logPrefix} Anchor-targeted display dropped: anchor "%s" not found in DOM`, anchor)
      continue
    }

    const update = parseTargetUpdate(contentOrObj)
    if (!update) continue

    if (update.contentType === 'text') {
      if (update.type === 'append') {
        el.textContent = `${el.textContent || ''}${update.content}`
      } else {
        el.textContent = update.content
      }
    } else {
      const sanitized = sanitizeAnearHtml(update.content)
      if (update.type === 'append') {
        el.insertAdjacentHTML('beforeend', sanitized)
      } else {
        el.innerHTML = sanitized
      }
    }
    anyApplied = true
  }

  if (anyApplied) {
    upgradeCustomElements(root)
  }

  return anyApplied
}

/**
 * Apply a display payload to a live EventFrame root.
 * `{ targets }` patches named anchors only — never rewrite the frame html.
 * `{ content }` is a full-page replace.
 */
export const applyFrameDisplay = (root, data, { onFullHtml, onRevision, logPrefix } = {}) => {
  const payload = data || {}
  const { content, targets } = payload

  if (targets != null && typeof targets === 'object' && !Array.isArray(targets)) {
    const anyApplied = applyAnchorTargets(root, targets, { logPrefix })
    if (anyApplied) onRevision?.()
    return anyApplied
  }

  if (content) {
    onFullHtml?.(content)
    onRevision?.()
    return true
  }

  return false
}

